/**
 * Creation v2 (docs/CREATION-V2.md, docs/API.md "Creation v2"): the live ticker check, the launch (identity + the ETH
 * creation fee in one wallet transaction) and its status, polled until the token is live and the basics are written.
 */
import { queryOptions, useQuery } from '@tanstack/react-query'
import type { CreationLaunch } from '@algoboo/schema/api'
import { request } from '../client'
import { qk } from '../keys'
import { invalidate, useApiMutation } from './mutations'

export const creationKeys = {
  ticker: (ticker: string, name: string) => ['catalog', 'ticker-available', ticker, name] as const,
  launch: (id: string) => ['me', 'creation', 'launch', id] as const,
  terms: () => ['catalog', 'launch-terms'] as const,
  refunds: () => ['me', 'refunds'] as const,
}

/**
 * GET /agents/launch-terms (public): whether the platform can launch the token now, and so what creating an agent
 * charges (the creation fee with a launch, nothing without one; lane ALGOBOO1, FEE1).
 */
export const launchTermsQuery = () => queryOptions({ queryKey: creationKeys.terms(), queryFn: ({ signal }) => request('getLaunchTerms', { signal }), staleTime: 30_000 })

export function useLaunchTerms() {
  return useQuery(launchTermsQuery())
}

/** GET /me/refunds: the platform's refunds of my creation fees charged for launches it refused. */
export function useMyRefunds(enabled = true) {
  return useQuery({ queryKey: creationKeys.refunds(), queryFn: ({ signal }) => request('listMyRefunds', { signal }), enabled, staleTime: 30_000 })
}

/** GET /agents/ticker-available (public): whether the ticker is free, with a suggestion. Callers debounce. */
export function useTickerAvailability(ticker: string, name: string, enabled = true) {
  return useQuery({
    queryKey: creationKeys.ticker(ticker, name),
    queryFn: ({ signal }) => request('getTickerAvailability', { signal, query: { ticker, ...(name ? { name } : {}) } }),
    enabled: enabled && ticker.length > 0,
    staleTime: 10_000,
  })
}

/** Whether a launch still changes on its own (payment, token, basics): poll it until it settled. */
export function launchSettled(l: CreationLaunch | undefined): boolean {
  if (!l) return false
  if (l.status === 'expired') return true
  if (l.status !== 'launched') return false
  return (l.token.status === 'live' || l.token.status === 'failed' || l.token.status === 'unavailable') && l.basicsStatus !== 'pending'
}

/** GET /agents/launch/:id, every `pollMs` until it settled. */
export function useLaunch(id: string | null, pollMs = 2000) {
  return useQuery({
    queryKey: creationKeys.launch(id ?? ''),
    queryFn: ({ signal }) => request('getLaunch', { signal, params: { id: id! } }),
    enabled: !!id,
    refetchInterval: (q) => (launchSettled(q.state.data) ? false : pollMs),
  })
}

/** POST /agents/launch (idempotent by the body's `clientKey`). */
export function useLaunchAgent(onDone: (l: CreationLaunch) => void) {
  return useApiMutation('launchAgent', { invalidate: () => [...invalidate.createAgent(), qk.creationCredits(), ['me', 'agents']], onSuccess: (l) => onDone(l) })
}

/**
 * GET /agents/:handle/operations (owner, lane ALGOBOO1): the agent's on-chain operations on the platform -- failed and
 * retried ones included -- polled every 15 s while one is under way.
 */
export function useAgentOperations(handle: string, enabled = true) {
  return useQuery({
    queryKey: ['agent', handle, 'operations'] as const,
    queryFn: ({ signal }) => request('listAgentOperations', { signal, params: { handle } }),
    enabled,
    refetchInterval: (q) => (q.state.data?.operations.some((o) => o.state === 'planned' || o.state === 'running') ? 15_000 : false),
  })
}

/** POST /agents/:handle/operations/:operationRef/retry (owner): a failed operation sent again. */
export function useRetryOperation(handle: string) {
  return useApiMutation('retryAgentOperation', { invalidate: () => [['agent', handle, 'operations'], qk.agent(handle)] })
}
